import { Link } from "@tanstack/react-router";
import { Facebook, Instagram, Twitter, MessageCircle, Coffee } from "lucide-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { fetchSettings, fetchCategories } from "@/lib/api";

export function SiteFooter() {
  const { data: settings } = useSuspenseQuery({
    queryKey: ["settings"],
    queryFn: fetchSettings,
  });

  const { data: categories } = useSuspenseQuery({
    queryKey: ["categories"],
    queryFn: fetchCategories,
  });

  const sortedCategories = categories 
    ? [...categories]
        .filter((c: any) => (c._count?.posts || 0) > 0)
        .sort((a: any, b: any) => a.name.localeCompare(b.name)) 
    : [];

  const siteName = settings?.site_name || "Lihar Aksara";
  const siteDesc = settings?.site_description || "Ruang sunyi untuk membaca dan merenung.";
  const twitter = settings?.social_twitter;
  const instagram = settings?.social_instagram;
  const whatsapp = settings?.whatsapp_number;

  let siteFirst = "";
  let siteRest = "";

  if (siteName.includes(" ")) {
    const parts = siteName.split(" ");
    siteFirst = parts[0];
    siteRest = parts.slice(1).join(" ");
  } else if (siteName.toLowerCase().startsWith("lihar")) {
    siteFirst = siteName.substring(0, 5);
    siteRest = siteName.substring(5);
  } else {
    siteFirst = siteName;
  }

  const branding = (
    <div className="flex items-center">
      <span className="font-logo text-2xl font-bold tracking-tight text-foreground">
        {siteFirst}
      </span>
      <span className="font-logo text-2xl font-bold tracking-tight text-accent">
        {siteRest ? (siteName.includes(" ") ? ` ${siteRest}` : siteRest) : ""}
      </span>
    </div>
  );

  return (
    <footer className="bg-background border-t border-border mt-24">
      <div className="container-editorial py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* Brand & About */}
          <div className="lg:col-span-1">
            <Link to="/">{branding}</Link>
            <p className="mt-6 text-sm text-muted-foreground font-serif italic leading-relaxed max-w-xs">
              {siteDesc}
            </p>
            <div className="mt-8 flex items-center gap-5">
              {instagram && (
                <a
                  aria-label="Instagram" href={`https://instagram.com/${instagram}`}
                  target="_blank"
                  rel="noreferrer"
                  className="w-11 h-11 flex items-center justify-center rounded-full border border-border text-muted-foreground hover:bg-accent hover:text-accent-foreground hover:border-accent transition-all"
                >
                  <Instagram size={16} className=" " />
                </a>
              )}
              {twitter && (
                <a
                  aria-label="Twitter" href={`https://twitter.com/${twitter}`}
                  target="_blank"
                  rel="noreferrer"
                  className="w-11 h-11 flex items-center justify-center rounded-full border border-border text-muted-foreground hover:bg-accent hover:text-accent-foreground hover:border-accent transition-all"
                >
                  <Twitter size={16} className=" " />
                </a>
              )}
              {whatsapp && (
                <a
                  aria-label="WhatsApp" href={`https://wa.me/${whatsapp}`}
                  target="_blank"
                  rel="noreferrer"
                  className="w-11 h-11 flex items-center justify-center rounded-full border border-border text-muted-foreground hover:bg-accent hover:text-accent-foreground hover:border-accent transition-all"
                >
                  <MessageCircle size={16} className=" " />
                </a>
              )}
            </div>
          </div>

                    {/* Navigasi */}
          <div>
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] mb-8 text-foreground/80">Navigasi</h4>
            <ul className="space-y-4">
              <li><Link to="/" className="text-sm text-muted-foreground hover:text-accent transition-colors">Beranda</Link></li>
              <li><Link to="/artikel" className="text-sm text-muted-foreground hover:text-accent transition-colors">Artikel</Link></li>
              <li className="space-y-2">
                <span className="text-sm text-foreground/80 font-medium">Sastra</span>
                <ul className="flex flex-col gap-2 pl-3 border-l border-border/50">
                  <li><Link to="/novel" className="text-sm text-muted-foreground hover:text-accent transition-colors">Novel</Link></li>
                  <li><Link to="/cerpen" className="text-sm text-muted-foreground hover:text-accent transition-colors">Cerpen</Link></li>
                  <li><Link to="/puisi" className="text-sm text-muted-foreground hover:text-accent transition-colors">Puisi</Link></li>
                </ul>
              </li>
              <li><Link to="/tentang" className="text-sm text-muted-foreground hover:text-accent transition-colors">Tentang Kami</Link></li>
            </ul>
          </div>

          {/* Kategori */}
          <div>
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] mb-8 text-foreground/80">Kategori</h4>
            <ul className="space-y-4">
              {sortedCategories.slice(0, 5).map((category: any) => (
                <li key={category.id}>
                  <Link to="/kategori/$slug" params={{ slug: category.slug }} className="text-sm text-muted-foreground hover:text-accent transition-colors">
                    {category.name}
                  </Link>
                </li>
              ))}
              {sortedCategories.length > 5 && (
                <li>
                  <Link to="/kategori" className="text-sm text-foreground hover:text-accent font-bold transition-colors">
                    Lihat Semua &rarr;
                  </Link>
                </li>
              )}
            </ul>
          </div>

          {/* Dukungan */}
          <div>
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] mb-8 text-foreground/80">Dukungan</h4>
            <p className="text-sm text-muted-foreground mb-6 font-serif">
              Bantu kami untuk terus menghidupkan ruang literasi yang sunyi namun berisi.
            </p>
            {settings?.donation_url && (
              <a
                href={settings.donation_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-ink text-paper text-xs font-bold uppercase tracking-widest hover:bg-accent transition-all shadow-lg shadow-black/20"
              >
                <Coffee size={16} className=" " />
                Dukung Kami
              </a>
            )}
          </div>
        </div>

        <div className="mt-20 pt-8 border-t border-border/50 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-6 text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
            <Link to="/privacy-policy" className="hover:text-accent transition-colors">Privacy</Link>
            <Link to="/terms-conditions" className="hover:text-accent transition-colors">Terms</Link>
            <Link to="/disclaimer" className="hover:text-accent transition-colors">Disclaimer</Link>
          </div>
          
          <div className="flex flex-col items-center md:items-end gap-2">
            <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground" suppressHydrationWarning>
              © {new Date().getFullYear()} {siteName}
            </p>
            <p className="text-xs font-serif italic text-muted-foreground">Selamat membaca di ruang sunyi.</p>
          </div>
        </div>
      </div>
    </footer>
  );
}
